/* eslint-disable react-hooks/exhaustive-deps */
import Feature from "@/components/feature";
import MainComposition from "@/components/mainComposition";
import { useWindowSize } from "@/hooks";
import useTrans from "@/hooks/useTrans";
import {
  LayoutDataPage,
  LocalStorageKey,
  setRootFunctions,
} from "@/services/constants";
import { getRequestHost } from "@/lib/request-host";
import { HttpStatus } from "@/lib/http-status";
import { getLayout } from "@/services/services";
import {
  Box,
  Center,
  Loader,
  Stack,
} from "@mantine/core";
import {
  OptimizedImage as Image,
  OptimizedBackgroundImage as BackgroundImage,
} from "@/components/helpers/optimizedImage/optimizedImage";
import convert from "color-convert";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Error404Page from "./404";
import ErrorBoundary from "@/components/errorPage/errorBoundary";
import dayjs from "dayjs";

// import Carousel from '@/components/carousel/carouel'

export const getServerSideProps = (async (context) => {
  const oldTenant = "";
  let hostNameUrl = getRequestHost(context.req.headers);

  if (oldTenant) {
    const oldUrl = localStorage.getItem(oldTenant);
    hostNameUrl = oldUrl ? oldUrl : hostNameUrl;
  } else {
    hostNameUrl = getRequestHost(context.req.headers);
  }

  const layoutFromApi = await getLayout(hostNameUrl);
  let testLayout: LayoutDataPage | null = null;
  let notFound = false;

  if (
    layoutFromApi &&
    layoutFromApi.layout &&
    Array.isArray(layoutFromApi.layout.pages) &&
    layoutFromApi.layout.pages.length > 0
  ) {
    testLayout = {
      url: hostNameUrl,
      page: null,
      config: layoutFromApi.layout.config,
      headers: layoutFromApi.layout.headers,
      footers: layoutFromApi.layout.footers,
      tenant: layoutFromApi.tenant,
    };

    // Same match the client does below. Only decided when the layout loaded:
    // a backend outage keeps the old client-side behavior instead of 404ing.
    // Not `return { notFound: true }`: with app/ present, Next renders the App
    // Router's bare default not-found for that, not pages/404.tsx.
    notFound = !layoutFromApi.layout.pages.some(
      (page: any) => page.href === `/${context.params?.id}`,
    );
    if (notFound) {
      context.res.statusCode = HttpStatus.NOT_FOUND;
    }
  }

  console.log("hostnameUrl", hostNameUrl);

  return {
    props: {
      hostNameUrl,
      testLayout,
      notFound,
    },
  };
}) satisfies GetServerSideProps<{
  hostNameUrl: string;
  testLayout: LayoutDataPage | null;
  notFound: boolean;
}>;

export default function Home({
  hostNameUrl,
  testLayout,
  notFound,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const trans = useTrans();

  const windowDimension = useWindowSize();
  const [isLoading, setIsLoading] = useState(true);

  const [layout, setLayout] = useState<LayoutDataPage | null>(null);
  const [layoutDataPages, setLayoutDataPages] = useState<any[]>([]);
  const router = useRouter();
  const { id } = router.query;

  useEffect(() => {
    getLayout().then((layoutFromApi) => {
      if (
        layoutFromApi &&
        layoutFromApi.layout &&
        Array.isArray(layoutFromApi.layout.pages) &&
        layoutFromApi.layout.pages.length > 0
      ) {
        setLayoutDataPages(layoutFromApi.layout.pages);
        setRootFunctions(layoutFromApi.layout.config);
        setLayout({
          url: hostNameUrl,
          page: layoutFromApi.layout.pages,
          config: layoutFromApi.layout.config,
          headers: layoutFromApi.layout.headers,
          footers: layoutFromApi.layout.footers,
          tenant: layoutFromApi.tenant,
        });
      } else {
        setIsLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    if (layout && layoutDataPages.length > 0) {
      const copyLayout = { ...layout };
      copyLayout.page = layoutDataPages.find((page) => page.href === `/${id}`);
      if (copyLayout?.page?.redirect) {
        setIsLoading(true);
        window.location.href = copyLayout.page.redirect;
        return;
      }
      console.log("layout for page...", copyLayout.page);
      setLayout({ ...copyLayout });
      setIsLoading(false);
    }
  }, [layoutDataPages, router.asPath]);

  let contentWidth: number = layout?.config?.contentWidth || 1440;
  let backgroundColor = "#fff";
  if (layout?.config.primaryColor) {
    const convertColor = convert.hex.rgb(layout?.config.primaryColor || "");
    backgroundColor = `rgba(${convertColor[0]}, ${convertColor[1]}, ${convertColor[2]},0.1)`;
  }

  const featureContent = (
    <Stack
      spacing={0}
      style={{
        background: layout?.config.background || "#F2F6F9",
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      {layout?.page?.body?.map((ele: any, index: number) => {
        const featureJSX = (
          <div
            style={{
              maxWidth: windowDimension.width <= 758 ? "100%" : contentWidth,
              width: windowDimension.width <= 758 ? "100%" : contentWidth,
              ...ele.options?.style,
            }}
          >
            {/* One broken block renders nothing; the rest of the page stays. */}
            <ErrorBoundary name={`block ${ele.component}/${ele.display}`}>
            <Feature
              url={layout?.url}
              display={ele.display}
              component={ele.component}
              options={ele.options}
              {...layout?.config}
              backgroundColor={backgroundColor}
              windowDimension={windowDimension}
              tenant={layout?.tenant}
            />
            </ErrorBoundary>
          </div>
        );

        if (ele.options?.backgroundImage) {
          return (
            <BackgroundImage
              key={index}
              src={ele.options?.backgroundImage}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {featureJSX}
            </BackgroundImage>
          );
        }

        return (
          <div
            key={index}
            style={{
              background: ele.options.background,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {featureJSX}
          </div>
        );
      })}
    </Stack>
  );

  const mainContent = isLoading ? (
    <Center style={{ height: "100vh" }}>
      <Stack>
        {layout ? (
          <Center>
            <Image
              src={layout?.config.logo}
              alt="logo"
              style={{ maxWidth: 500 }}
            />
          </Center>
        ) : (
          <></>
        )}

        <Center>
          <Loader variant="dots" color={layout?.config.primaryColor} />
        </Center>
      </Stack>
    </Center>
  ) : (
    <MainComposition
      display={"main-composition1"}
      config={layout?.config}
      headers={layout?.headers}
      footers={layout?.footers}
      mainContent={featureContent}
    />
  );

  if (notFound) {
    return <Error404Page />;
  }

  return isLoading || (!isLoading && layout && layout.page) ? (
    <>
      <Head>
        <title>{layout?.page.name || testLayout?.config?.metaTitle}</title>
        <meta
          name="description"
          content={testLayout?.config?.metaDescription || "Trang CMS"}
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="icon"
          href={testLayout?.config?.favicon || layout?.config.favicon}
        />
        <meta
          name="google-site-verification"
          content={testLayout?.config?.googleSiteVerification}
        />

        <meta property="og:title" content={testLayout?.config?.metaTitle} />
        <meta
          property="og:description"
          content={testLayout?.config?.metaDescription || "Trang CMS"}
        />
        <meta
          property="og:image"
          content={testLayout?.config?.metaImage || testLayout?.config?.logo}
        ></meta>
      </Head>
      <Box>{mainContent}</Box>
    </>
  ) : (
    // <p>{"[lang]"}</p>
    <Error404Page />
  );
  //   return <p>{'[lang]'}</p>
}
